// Place in: src/components/SmsSyncBanner.jsx
// Shows a banner on mobile when there are pending SMS to send

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Sms, Close } from '@mui/icons-material';
import api from '../api/authApi';

const isMobile = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

export default function SmsSyncBanner() {
  const [pendingCount, setPendingCount] = useState(0);
  const [pending, setPending]           = useState([]);
  const [sending, setSending]           = useState(false);
  const [currentIdx, setCurrentIdx]     = useState(0);
  const [done, setDone]                 = useState(false);
  const [dismissed, setDismissed]       = useState(false);

  // Fetch pending count on mount (all devices) for stats
  useEffect(() => {
    api.get('/sms-queue/stats')
      .then(r => {
        if (r.data?.success) setPendingCount(r.data.data?.totalPending || 0);
      })
      .catch(() => {});
  }, []);

  // Fetch full pending list when user clicks Send on mobile
  const loadPending = useCallback(async () => {
    const res = await api.get('/sms-queue/pending');
    if (res.data?.success) {
      setPending(res.data.data || []);
      return res.data.data || [];
    }
    return [];
  }, []);

  const startSending = useCallback(async () => {
    setSending(true);
    setCurrentIdx(0);
    const list = await loadPending();
    if (!list.length) { setSending(false); setDone(true); return; }

    const sentIds = [];
    for (let i = 0; i < list.length; i++) {
      setCurrentIdx(i + 1);
      const sms = list[i];
      // Open native SMS app
      window.location.href = `sms:${sms.mobile}?body=${encodeURIComponent(sms.message)}`;
      sentIds.push(sms.id);
      // Wait between messages
      await new Promise(res => setTimeout(res, 2000));
    }

    // Mark all as sent
    if (sentIds.length > 0) {
      await api.put('/sms-queue/mark-sent-bulk', { smsIds: sentIds });
    }

    setSending(false);
    setPendingCount(0);
    setDone(true);
  }, [loadPending]);

  if (dismissed) return null;
  if (pendingCount === 0 && !done) return null;

  // Show success toast
  if (done) return (
    <Snackbar open autoHideDuration={4000} onClose={() => setDone(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert severity="success" onClose={() => setDone(false)}>
        All SMS sent successfully! ✅
      </Alert>
    </Snackbar>
  );

  // Desktop: show info banner (no send button, just info)
  if (!isMobile()) return (
    <Box sx={{
      background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)',
      border: '1px solid #a5d6a7',
      borderRadius: 2, px: 2, py: 1.2, mb: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Sms sx={{ color: '#388e3c', fontSize: 20 }} />
        <Typography sx={{ fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
          {pendingCount} SMS pending — will be sent automatically when you open on mobile
        </Typography>
      </Box>
      <Button size="small" onClick={() => setDismissed(true)}
        sx={{ minWidth: 'auto', color: '#888', p: 0.3 }}>
        <Close sx={{ fontSize: 16 }} />
      </Button>
    </Box>
  );

  // Mobile: show send button
  return (
    <Box sx={{
      background: 'linear-gradient(90deg, #e3f2fd, #e8eaf6)',
      border: '1px solid #90caf9',
      borderRadius: 2, px: 2, py: 1.5, mb: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Sms sx={{ color: '#1976d2', fontSize: 20 }} />
        <Box>
          <Typography sx={{ fontSize: 13, color: '#1565c0', fontWeight: 700 }}>
            {sending
              ? `Sending SMS ${currentIdx} of ${pending.length}...`
              : `${pendingCount} SMS ready to send`}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#1976d2' }}>
            {sending ? 'Please keep the app open' : 'Tap to send via your SIM card'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {sending
          ? <CircularProgress size={22} sx={{ color: '#1976d2' }} />
          : <>
              <Button variant="contained" size="small" onClick={startSending}
                sx={{ background: '#1976d2', fontSize: 12, textTransform: 'none', px: 1.5 }}>
                Send Now
              </Button>
              <Button size="small" onClick={() => setDismissed(true)}
                sx={{ minWidth: 'auto', color: '#888', p: 0.3 }}>
                <Close sx={{ fontSize: 16 }} />
              </Button>
            </>
        }
      </Box>
    </Box>
  );
}